import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createLicense, GetLicensesList, RefrechLicense, SendLicense, updateLicense } from '../api/licenses';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Chip from '../components/Chip';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '../components/Dialog';
import Snackbar from '../components/Snackbar';
import { useAuth } from '../store/auth';
import RowActions from '../components/RowActions';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import PageHeaderPortal from '../components/PageHeaderPortal';

export default function Dashboard() {
  const [rows, setRows] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()+1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [currentSettleVendor, setCurrentSettleVendor] = useState(null);
  const [settling, setSettling] = useState(false);

  const { user } = useAuth();
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const isCurrentMonth = selectedMonth === currentMonth && selectedYear === currentYear;
 
  // CHIP STATUS
  const LicenseStatusChip = React.memo(({ date, isNewAcount }) => {
    if (isNewAcount === "1" || isNewAcount === 1)
      return <Chip color="default" size="small" label="Nie wysłano" />;
    if (!date) return <Chip color="default" size="small" label="Brak daty" />;
    const parsed = new Date(String(date).replace(' ', 'T'));
    const expired = parsed < new Date();
    return expired ? <Chip color="error" size="small" label="Wygasła" /> : <Chip color="success" size="small" label="Aktywna" />;
  });

  // 🔹 POBIERANIE DANYCH
  useEffect(() => {
    setLoading(true);
    GetLicensesList()
      .then(data => {
        const mapped = (data.data || []).map(item => ({
          id: item.subscriber_id,
          name: item.license_name,
          client_email: item.client_email,
          accountManager: item.us_name,
          shop_expireDate: item.shop_expireDate,
          last_login: item.last_login,
          shop_name: item.shop_name,
          active: item.active,
          newAcount: item.newAcount,
        }));
        setRows(mapped);
      })
      .catch(console.error);

    fetchRenewals()
      .then(data => {
        if (Array.isArray(data)) setRenewals(data);
        else if (data?.renewals) setRenewals(data.renewals);
      })
      .catch(err => console.warn('Nie udało się pobrać renewals:', err))
      .finally(() => setLoading(false));
  }, []);

  const fetchRenewals = async () => {
    const res = await fetch('https://hs2api.razamit.pl/renewalsList');
    if (!res.ok) throw new Error('Błąd pobiernia danych przedłużeń');
    return await res.json();
  };

  const settleRenewals = async (ids = []) => {
    const res = await fetch('https://hs2api.razamit.pl/renewals/settle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error('Nie udało się oznaczyć jako settled');
    return res.json();
  };

  // 📊 FILTROWANIE wg MIESIĄCA I ROKU
  const filteredRenewals = useMemo(() => {
    return renewals.filter(r => {
      if (r.price <= 0) return false;
      const d = new Date(String(r.renewal_date).replace(' ', 'T'));
      return d.getMonth()+1 === selectedMonth && d.getFullYear() === selectedYear;
    });
  }, [renewals, selectedMonth, selectedYear]);

  const monthLabel = useMemo(() => {
    const d = new Date(selectedYear, selectedMonth - 1);
    return d.toLocaleString('pl-PL', { month: 'long', year: 'numeric' });
  }, [selectedMonth, selectedYear]);

  // 📈 STATYSTYKI
  const monthStats = useMemo(() => {
    const sent = filteredRenewals.filter(r =>
      r.price > 0 &&
      (r.notes === 'Pierwsze wystawienie licencji' || r.status === 'sent' || r.event === 'sent')
    ).length;

    const activated = filteredRenewals.filter(r =>
      r.price > 0 &&
      (r.notes === 'Odnowiono licencję' || r.status === 'activated' || r.event === 'activated')
    ).length;

    const totalPrice = filteredRenewals.reduce((a, r) => a + Number(r.price), 0);
    const settledCount = filteredRenewals.filter(r => Number(r.settled) === 1).length;

    return { sent, activated, total: filteredRenewals.length, totalPrice, settledCount };
  }, [filteredRenewals]);

  const isMonthSettled = monthStats.settledCount === filteredRenewals.length && filteredRenewals.length >= 0;

  // 📆 SELECTY MIESIĄCA
  const months = [
    { value: 1, label: 'Styczeń' }, { value: 2, label: 'Luty' }, { value: 3, label: 'Marzec' },
    { value: 4, label: 'Kwiecień' }, { value: 5, label: 'Maj' }, { value: 6, label: 'Czerwiec' },
    { value: 7, label: 'Lipiec' }, { value: 8, label: 'Sierpień' }, { value: 9, label: 'Wrzesień' },
    { value: 10, label: 'Październik' }, { value: 11, label: 'Listopad' }, { value: 12, label: 'Grudzień' },
  ];
  

  
  // 🔒 Filtrowanie — nie pokazuj miesięcy przyszłych względem bieżącego roku
  const allowedMonths = months.filter(m => {
    if (selectedYear < currentYear) return true; // wcześniejsze lata -> wszystkie miesiące OK
    if (selectedYear === currentYear) return m.value <= currentMonth; // tylko do aktualnego miesiąca
    return false; // przyszłe lata -> brak miesięcy
  });

  const MonthSelector = (
    <div className="flex gap-3 mb-3 items-center">
    <Select
      value={selectedMonth}
      onChange={e => setSelectedMonth(Number(e.target.value))}
      options={allowedMonths.map(m => ({ value: m.value, label: m.label }))}
    />
    <Input
      type="number"
      min="2023"
      max={currentYear} // 🔒 nie pozwalamy wpisać roku w przyszłości
      value={selectedYear}
      onChange={e => {
        const newYear = Number(e.target.value);
        if (newYear > currentYear) return; // blokada wpisania przyszłego roku
        setSelectedYear(newYear);
      }}
      className="w-24"
    />
  </div>
  );

  // 📊 ROZLICZENIA VENDORÓW
  const breakdown = useMemo(() => {
    const byVendor = {};
    filteredRenewals.forEach(r => {
      if (Number(r.settled) === 1) return;
      const vendor = r.renewed_by || r.supplier || 'Nieznane';
      if (!byVendor[vendor]) byVendor[vendor] = [];
      byVendor[vendor].push(r);
    });

    const vendors = Object.keys(byVendor).map(v => {
      const items = byVendor[v];
      const count = items.length;
      const base = count * 100;
      const bonus = count > 2 ? Math.floor(count / 2) * 1 : 0;
      const totalForVendor = base + bonus;
      return { vendor: v, count, base, bonus, totalForVendor, items };
    });

    const totalPrice = filteredRenewals.reduce((acc, r) => acc + Number(r.price), 0);
    const totalVendorsSum = vendors.reduce((a, v) => a + v.totalForVendor, 0);
    const remainder = Math.max(0, totalPrice - totalVendorsSum);
    const splitA = +(remainder / 2).toFixed(2);
    const splitB = +(remainder - splitA).toFixed(2);

    return { vendors, totalVendorsSum, remainder, splitA, splitB };
  }, [filteredRenewals]);

  const openSettleForVendor = vendor => {
    setCurrentSettleVendor(vendor);
    setSettleModalOpen(true);
  };

  const handleSettleConfirm = useCallback(async () => {
    if (!currentSettleVendor) return;
    setSettling(true);
    try {
      const ids = currentSettleVendor.items.map(i => i.id);
      await settleRenewals(ids);
      const refreshed = await fetchRenewals();
      setRenewals(refreshed);
      setToast({ open: true, message: 'Rozliczono pomyślnie', severity: 'success' });
      setSettleModalOpen(false);
    } catch (err) {
      setToast({ open: true, message: 'Błąd rozliczania', severity: 'error' });
    } finally {
      setSettling(false);
    }
  }, [currentSettleVendor]);

  return (
    <div>
      <PageHeaderPortal title="Rozliczenie licensji dystrybutorów" subtitle="Rozliczenie licencji dystrybutorów wraz z informacją odnoścnie kosztów marketingu" />

      {MonthSelector}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <StatCard label={`${monthLabel}`} value={`${monthStats.total}`} />
        <StatCard label="Wystawiono" value={monthStats.sent} />
        <StatCard label="Odnowiono" value={monthStats.activated} />
        <StatCard label="Przychód" value={`${monthStats.totalPrice.toFixed(2)} zł`} />
      </div>

      <div className={`p-3 rounded ${isMonthSettled ? 'bg-green-50 border-green-400 border' : 'bg-yellow-50 border-yellow-400 border'} mb-4`}>
        {isMonthSettled ? (
          <p className="text-green-700 font-medium">✅ Miesiąc {monthLabel} został w całości rozliczony.</p>
        ) : (
          <p className="text-yellow-700 font-medium">⚠️ Miesiąc {monthLabel} nie jest jeszcze w pełni rozliczony.</p>
        )}
      </div>

      {/* PANEL ROZLICZEŃ */}
      <div className="w-full rounded-sm bg-white p-4 border border-gray-200 mb-4 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-2">Przedłużenia — {monthLabel}</h3>
          <p className="text-sm text-gray-600 mb-3">
            Nierozliczone: <strong>{breakdown.vendors.reduce((a, v) => a + v.count, 0)}</strong> •
            Do rozliczenia: <strong>{monthStats.totalPrice.toFixed(2)} PLN</strong>
          </p>

          <div className="grid grid-cols-1 gap-2">
            {breakdown.vendors.map(v => (
              <div key={v.vendor} className="p-3 border rounded flex justify-between items-center">
                <div>
                  <div className="font-medium">{v.vendor}</div>
                  <div className="text-sm text-gray-500">{v.count} przedłużeń • baza: {v.base} zł • bonus: {v.bonus} zł</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right mr-4">
                    <div className="font-medium">{v.totalForVendor} zł</div>
                    <div className="text-xs text-gray-500">suma dla dostawcy</div>
                  </div>
                  {isCurrentMonth ? (
  <span className="text-xs text-gray-500">Nie można rozliczyć trwającego miesiąca</span>
) : (
  <Button variant="outlined" onClick={() => openSettleForVendor(v)}>Rozlicz</Button>
)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ minWidth: 320 }} className="bg-gray-50 p-3 rounded">
          <h4 className="font-medium mb-2">Podział reszty puli</h4>
          <p className="text-sm text-gray-700">Suma vendorów: <strong>{breakdown.totalVendorsSum} PLN</strong></p>
          <p className="text-sm text-gray-700">Reszta do podziału: <strong>{breakdown.remainder} PLN</strong></p>
          <div className="mt-3">
            <div className="flex justify-between"><span>Firma A</span><strong>{breakdown.splitA} PLN</strong></div>
            <div className="flex justify-between mt-1"><span>Firma B</span><strong>{breakdown.splitB} PLN</strong></div>
          </div>
        </div>
      </div>

      {/* MODAL ROZLICZEŃ */}
      <Dialog open={settleModalOpen} onClose={() => setSettleModalOpen(false)}>
        <DialogTitle>Rozlicz — {currentSettleVendor?.vendor}</DialogTitle>
        <DialogContent>
          {currentSettleVendor && (
            <>
              <p>Przedłużeń: <strong>{currentSettleVendor.count}</strong></p>
              <p>Suma bazowa: <strong>{currentSettleVendor.base} PLN</strong></p>
              <p>Bonus: <strong>{currentSettleVendor.bonus} PLN</strong></p>
              <p>Do wypłaty: <strong>{currentSettleVendor.totalForVendor} PLN</strong></p>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettleModalOpen(false)} variant="outlined">Anuluj</Button>
          <Button onClick={handleSettleConfirm} variant="contained" color="success" disabled={settling}>
            {settling ? 'Trwa...' : 'Potwierdź'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} message={toast.message} onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} />
    </div>
  );
}
